import { createRouter, createWebHistory } from "vue-router";
import { HomeScreen, GridScreen } from "@/components/screens";
import { HotspotModal } from "@/components/ui";
import store from "../store/index.js";
import { setActiveLanguage } from "./utils";
import { hreflangService } from "@/services/hreflang.service";
import { canonicalService } from "@/services/canonical.service";

const routes = [
	{
		path: "/",
		redirect: () => {
			const activeLanguage = store.getters["languages/activeLanguage"];
			return activeLanguage
				? `/${activeLanguage.two_digit_code}`
				: "/en";
		},
	},
	{ path: "/:lang", component: HomeScreen, name: "HomeScreen" },
	{
		path: "/:lang/:category/:sector",
		component: GridScreen,
		name: "SectorScreen",
	},
	{
		path: "/:lang/:category/:sector/:layer",
		component: GridScreen,
		name: "LayerScreen",
		children: [
			{
				path: ":hotspotSlug",
				component: HotspotModal,
				name: "HotspotModal",
				props: true,
			},
		],
	},
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes,
});

router.beforeEach(async (to, from, next) => {
	// Pass URL language to the store
	await setActiveLanguage(store, to.params.lang);

	const activeLanguage = store.getters["languages/activeLanguage"];

	if (to.path === "/") {
		if (activeLanguage) {
			return next({ path: `/${activeLanguage.two_digit_code}` });
		}
		return next();
	}

	if (
		["diverseygps.com", "www.diverseygps.com"].includes(
			window.location.hostname,
		)
	) {
		window.location.href = `https://gps.solenis.com${to.fullPath}`;
		return;
	}

	if (to.name === "LayerScreen" && to.params.layer) {
		const layers = store.getters.layers;
		const layerParam = String(to.params.layer).trim();
		const isNumericLayer = /^\d+$/.test(layerParam);
		const layerById = layers.find((l) => l.id === layerParam);
		const layerByNumericId = isNumericLayer
			? layers.find((l) => l.layer_id === Number(layerParam))
			: null;
		const layer = layerById ?? layerByNumericId;

		if (layerByNumericId && layerByNumericId.id !== layerParam) {
			return next({
				...to,
				params: { ...to.params, layer: layerByNumericId.id },
				replace: true,
			});
		}

		const activeLayer =
			layer ??
			(to.params.layer
				? {
						id: to.params.layer,
						name: to.params.layer.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
					}
				: null);
		if (activeLayer) {
			store.dispatch("setActiveLayer", activeLayer);
		}
	}

	if (!to.params.lang && activeLanguage) {
		return next({
			...to,
			params: { ...to.params, lang: activeLanguage.two_digit_code },
		});
	}

	// Only redirect if there's a stored language preference that differs from URL
	// If no stored language, respect the URL language
	const hasStoredLanguage = localStorage.getItem("activeLanguage");
	if (
		to.params.lang &&
		activeLanguage &&
		to.params.lang !== activeLanguage.two_digit_code &&
		hasStoredLanguage
	) {
		return next({
			...to,
			params: { ...to.params, lang: activeLanguage.two_digit_code },
		});
	}

	return next();
});

router.afterEach((to) => {
	let pageTitle = "Diversey GPS";

	if (to.name === "HomeScreen") {
		pageTitle = "Diversey GPS - Home";
	} else if (to.name === "LayerScreen") {
		const { category, sector, layer } = to.params;
		const formattedCategory = category
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());
		const formattedSector = sector
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());
		const formattedLayer = layer
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());

		pageTitle = `Diversey GPS - ${formattedCategory} ${formattedSector} ${formattedLayer}`;
	} else if (to.name === "HotspotModal") {
		const { category, sector, layer, hotspotSlug } = to.params;
		const formattedCategory = category
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());
		const formattedSector = sector
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());
		const formattedLayer = layer
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());
		const formattedHotspot = hotspotSlug
			?.replace(/-/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());

		pageTitle = `Diversey GPS - ${formattedHotspot} | ${formattedCategory} ${formattedSector} ${formattedLayer}`;
	}

	document.title = pageTitle;

	// Generate and inject hreflang tags for SEO
	try {
		const languages = store.getters["languages/languages"];
		const languageCodes =
			languages.length > 0
				? languages.map((lang) => lang.two_digit_code)
				: null;

		hreflangService.generateAndInjectHreflangTags(to, languageCodes);
	} catch (error) {
		console.warn("Failed to generate hreflang tags:", error);
	}

	// Generate and inject canonical link for SEO
	try {
		canonicalService.generateAndInjectCanonicalLink(to);
	} catch (error) {
		console.warn("Failed to generate canonical link:", error);
	}
});

export default router;
